import { useCallback, useEffect, useMemo } from 'react';
import { useClickOutside } from '@sdk/common/hooks/useClickOutside';
import { useToggle } from '@sdk/common/hooks/useToggle';
import { useSession } from '@sdk/session/SessionContext';

import { MessagePost } from '@web/conversations/types/Message';
import { Emoji } from '@web/conversations/types/Reactions';
import { useConversationActions } from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { useDeleteMessageAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useDeleteMessageAction';
import { useMarkMessageAsUnreadAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useMarkMessageAsUnreadAction';
import { useAddReactionAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useAddReactionAction';
import { canApplyReaction } from '@web/conversations/Reactions/utils/canApplyReaction';

export const useMessageContextMenu = (message: MessagePost) => {
  const { currentUser } = useSession();
  const canReact = canApplyReaction({
    currentUserId: currentUser.id,
    reactions: message.reactions,
  });
  const { selectQuotedMessage, setEditMessage } = useConversationActions();
  const { action: deleteMessage } = useDeleteMessageAction();
  const { action: markMessageAsUnread } = useMarkMessageAsUnreadAction();
  const { action: addReaction } = useAddReactionAction();

  return useMemo(() => {
    const { conversationId, id: messageId } = message;

    return {
      canReact,
      markAsUnread: () => markMessageAsUnread({ conversationId, message }),
      remove: () => deleteMessage({ conversationId, messageId }),
      replyTo: () => selectQuotedMessage({ messageId }),
      selectReaction: (emoji: Emoji) => {
        addReaction({
          conversationId,
          emoji,
          messageId,
        });
      },
      openEdit: () => setEditMessage({ messageId }),
    };
  }, [
    addReaction,
    canReact,
    markMessageAsUnread,
    message,
    deleteMessage,
    selectQuotedMessage,
    setEditMessage,
  ]);
};

export type EmojiPickerProps = {
  lockMenu: () => void;
  onSelect: (reaction: Emoji) => void;
  unlockMenu: () => void;
};
export const useEmojiPicker = ({ lockMenu, onSelect, unlockMenu }: EmojiPickerProps) => {
  const { lockScroll, unlockScroll } = useConversationActions();
  const { isOn, setOff, toggle } = useToggle();
  const onPick = useCallback(
    (reaction: Emoji) => {
      onSelect(reaction);
      setOff();
    },
    [onSelect, setOff]
  );
  const ref = useClickOutside<HTMLDivElement>(setOff);
  useEffect(() => (isOn ? lockMenu() : unlockMenu()), [isOn, lockMenu, unlockMenu]);
  useEffect(
    () => void (isOn ? setTimeout(lockScroll) : unlockScroll()),
    [isOn, lockScroll, unlockScroll]
  );

  const position = useMemo(() => {
    if (!isOn) return;

    const rect = ref.current?.getBoundingClientRect();
    const minimumTopOffset = 320;

    const alingToBottom = rect.top < minimumTopOffset;
    const top = alingToBottom ? rect.bottom : undefined;
    const bottom = !alingToBottom ? window.innerHeight - rect.top : undefined;

    return {
      bottom,
      right: window.innerWidth - rect.right,
      top,
    };
  }, [isOn, ref]);

  return {
    toggle,
    isOn,
    onPick,
    position,
    ref,
  };
};
